import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import {
  useApproveTransaction,
  useCancelTransaction,
  useCompleteTransaction,
  useRaiseGrievance,
  useRequestRevision,
  useSendForApproval,
} from '@shared/hooks/use-transaction-actions';
import type { CancellationRequestStatus, TransactionStatus, Uuid } from '@shared/types/api';

const CLIENT_CANCELLABLE: readonly TransactionStatus[] = [
  'INITIATED',
  'SERVICE_REQUESTED',
  'CHAT_CREATED',
  'INVOICE_CREATED',
  'PAYMENT_PENDING',
  'PAID',
  'PAYMENT_RECEIVED',
  'ASSOCIATE_ASSIGNMENT_PENDING',
  'ASSOCIATE_ASSIGNED',
  'ASSOCIATE_ACCEPTANCE_PENDING',
  'ASSOCIATE_ACCEPTED',
  'CLIENT_CONFIRMATION_PENDING',
  'CLIENT_CONFIRMED',
  'IN_PROGRESS',
  'DRAFT_PREPARATION',
  'CLIENT_DRAFT_CONFIRMATION',
  'FINAL_RESULT_RECEIVED',
  'DELIVERED',
  'CLIENT_CONFIRMED_FINAL',
  'GRIEVANCE_RAISED',
  'ASSIGNED',
  'ASSIGNED_REVOKED',
  'REVIEW',
  'REVISION_REQUESTED',
  'SENT_FOR_APPROVAL',
];

// Interactive star rating selector
function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => setHovered(0)}
      role="radiogroup"
      aria-label="Star rating"
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          aria-checked={value === star}
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
          className="cursor-pointer focus:outline-none transition-transform hover:scale-110"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-8 h-8 transition-colors duration-100"
            fill={(hovered || value) >= star ? '#f59e0b' : 'none'}
            stroke={(hovered || value) >= star ? '#f59e0b' : '#d1d5db'}
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
            />
          </svg>
        </button>
      ))}
    </div>
  );
}

// Contextual action buttons on the chat header. Only shows buttons that
// correspond to state-machine transitions the client can trigger.
export function TransactionActions({
  transactionId,
  status,
  paidAt,
  cancellationRequestStatus,
}: {
  transactionId: Uuid;
  status: TransactionStatus;
  paidAt?: string | null;
  cancellationRequestStatus?: CancellationRequestStatus | null;
}) {
  const [dialog, setDialog] = useState<null | 'cancel' | 'revise' | 'complete'>(null);
  const [reason, setReason] = useState('');
  const [rating, setRating] = useState(0);
  const [feedbackDescription, setFeedbackDescription] = useState('');

  const directCancel = useCancelTransaction(transactionId);
  const raiseGrievance = useRaiseGrievance(transactionId);
  const approve = useApproveTransaction(transactionId);
  const revise = useRequestRevision(transactionId);
  const complete = useCompleteTransaction(transactionId);
  const acceptDraft = useSendForApproval(transactionId);

  const canCancel =
    CLIENT_CANCELLABLE.includes(status) &&
    status !== 'CANCELLED' &&
    status !== 'COMPLETE' &&
    status !== 'CLOSED' &&
    status !== 'SETTLED';
  // ONLY after Admin creates an invoice and marks paid (paidAt is set) does cancellation button become "Raise Grievance".
  // Before payment completion (when paidAt is null), client sees the "Cancel" button.
  const isGrievanceStage = Boolean(paidAt) || status === 'GRIEVANCE_RAISED';
  const isGrievancePending = isGrievanceStage && cancellationRequestStatus === 'PENDING';

  // DRAFT REVIEW / CONFIRMATION: client has the associate's draft.
  const canAcceptDraft = status === 'REVIEW' || status === 'CLIENT_DRAFT_CONFIRMATION';
  // FINAL SIGN-OFF / APPROVAL
  const canApprove = status === 'SENT_FOR_APPROVAL' || status === 'CLIENT_CONFIRMED_FINAL';
  // MARK COMPLETE
  const canComplete =
    status === 'FINAL_DOC_READY' || status === 'DELIVERED' || status === 'RATING_PENDING';
  // REVISION REQUEST
  const canRevise =
    status === 'REVIEW' ||
    status === 'CLIENT_DRAFT_CONFIRMATION' ||
    status === 'SENT_FOR_APPROVAL' ||
    status === 'DELIVERED' ||
    status === 'GRIEVANCE_RAISED';

  function openCompleteDialog() {
    setRating(0);
    setFeedbackDescription('');
    setDialog('complete');
  }

  function handleCompleteSubmit() {
    complete.mutate(
      { rating, feedbackDescription: feedbackDescription.trim() || undefined },
      { onSuccess: () => setDialog(null) },
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {canAcceptDraft ? (
          <Button
            className="h-8 px-3 py-1 text-xs"
            onClick={() => acceptDraft.mutate()}
            disabled={acceptDraft.isPending}
          >
            {acceptDraft.isPending ? 'Accepting…' : 'Accept draft'}
          </Button>
        ) : null}
        {canApprove ? (
          <Button
            className="h-8 px-3 py-1 text-xs"
            onClick={() => approve.mutate()}
            disabled={approve.isPending}
          >
            {approve.isPending ? 'Approving…' : 'Approve'}
          </Button>
        ) : null}
        {canRevise ? (
          <Button
            className="h-8 px-3 py-1 text-xs"
            variant="outline"
            onClick={() => {
              setReason('');
              setDialog('revise');
            }}
          >
            Request revision
          </Button>
        ) : null}
        {canComplete ? (
          <Button
            className="h-8 px-3 py-1 text-xs"
            onClick={openCompleteDialog}
            disabled={complete.isPending}
          >
            {complete.isPending ? 'Completing…' : 'Mark complete'}
          </Button>
        ) : null}
        {canCancel ? (
          <Button
            className={`h-8 px-3 py-1 text-xs font-medium ${
              isGrievancePending
                ? 'bg-amber-100 text-amber-800 border border-amber-300 cursor-not-allowed'
                : 'border border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700'
            }`}
            variant="outline"
            disabled={isGrievancePending}
            onClick={() => {
              setReason('');
              setDialog('cancel');
            }}
          >
            {!isGrievanceStage
              ? 'Cancel'
              : isGrievancePending
                ? 'Grievance Pending'
                : 'Raise Grievance'}
          </Button>
        ) : null}
      </div>

      {/* ── Mark-complete rating dialog ─────────────────────────── */}
      <Dialog
        open={dialog === 'complete'}
        onClose={() => setDialog(null)}
        title="Rate your experience"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button onClick={handleCompleteSubmit} disabled={rating === 0 || complete.isPending}>
              {complete.isPending ? 'Submitting…' : 'Mark complete'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 mb-4">
          How was your overall experience with this service?
        </p>

        {/* Star selector — rating is compulsory */}
        <div className="mb-1">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Rating <span className="text-red-500">*</span>
          </p>
          <StarRating value={rating} onChange={setRating} />
          {rating > 0 && (
            <p className="mt-1 text-xs text-amber-600 font-medium">
              {['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'][rating]}
            </p>
          )}
        </div>

        {/* Optional description */}
        <div className="mt-4">
          <label className="text-sm font-medium text-gray-700 block mb-1" htmlFor="feedback-desc">
            Comments <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            id="feedback-desc"
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Share more about your experience…"
            value={feedbackDescription}
            onChange={(e) => setFeedbackDescription(e.target.value)}
            maxLength={1000}
          />
          <p className="text-xs text-gray-400 text-right">{feedbackDescription.length}/1000</p>
        </div>

        {complete.isError ? (
          <p className="mt-2 text-sm text-red-600">{apiErrorMessage(complete.error)}</p>
        ) : null}
      </Dialog>

      {/* ── Cancel / Raise Grievance Dialog ──────────────────────── */}
      {!isGrievanceStage ? (
        /* Direct cancel dialog for INITIATED status */
        <Dialog
          open={dialog === 'cancel'}
          onClose={() => setDialog(null)}
          title="Cancel transaction"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog(null)}>
                Keep it
              </Button>
              <Button
                onClick={() => {
                  directCancel.mutate(reason, { onSuccess: () => setDialog(null) });
                }}
                disabled={!reason.trim() || directCancel.isPending}
                className="bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                {directCancel.isPending ? 'Cancelling…' : 'Cancel transaction'}
              </Button>
            </>
          }
        >
          <p className="text-sm text-gray-600">
            Let us know why you are cancelling this service request.
          </p>
          <Input
            className="mt-3"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for cancellation"
          />
          {directCancel.isError ? (
            <p className="mt-2 text-sm text-red-600">{apiErrorMessage(directCancel.error)}</p>
          ) : null}
        </Dialog>
      ) : (
        /* Raise Grievance / Cancellation Request Dialog for post-invoice / payment / in-progress status */
        <Dialog
          open={dialog === 'cancel'}
          onClose={() => setDialog(null)}
          title="Raise Grievance / Request Cancellation"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog(null)}>
                Back
              </Button>
              <Button
                onClick={() => {
                  raiseGrievance.mutate(reason.trim(), { onSuccess: () => setDialog(null) });
                }}
                disabled={!reason.trim() || raiseGrievance.isPending}
                className="bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                {raiseGrievance.isPending ? 'Submitting…' : 'Submit Grievance Request'}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-xs text-gray-600 leading-relaxed">
              Please state your detailed reason for requesting cancellation or raising a grievance.
              Your request will be sent directly to EcRegi Admin for verification and approval.
            </p>
            <textarea
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for grievance / cancellation..."
            />
            {raiseGrievance.isError ? (
              <p className="mt-1 text-xs text-red-600">{apiErrorMessage(raiseGrievance.error)}</p>
            ) : null}
          </div>
        </Dialog>
      )}

      {/* ── Request revision dialog ─────────────────────────────── */}
      <Dialog
        open={dialog === 'revise'}
        onClose={() => setDialog(null)}
        title="Request revision"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                revise.mutate(reason, { onSuccess: () => setDialog(null) });
              }}
              disabled={!reason.trim() || revise.isPending}
            >
              {revise.isPending ? 'Sending…' : 'Send request'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">What should the Associate change?</p>
        <Input
          className="mt-3"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Revision notes"
        />
        {revise.isError ? (
          <p className="mt-2 text-sm text-red-600">{apiErrorMessage(revise.error)}</p>
        ) : null}
      </Dialog>
    </>
  );
}
